import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { USER_MANAGEMENT_TABLE_COLUMN_CLASSES } from './constants';
import { UserManagementRoleCell } from './UserManagementRoleCell';
import { UserManagementRowSkeleton } from './UserManagementRowSkeleton';

import { MoreVerticalIcon } from '@/assets/icons';
import { IconButton, Tag } from '@/components/atoms';
import {
  Dropdown,
  DropdownMenuContent,
  DropdownMenuSimpleItem,
  DropdownMenuTrigger,
} from '@/components/organisms';
import { UUID } from '@/types/common';
import { UserRole } from '@/types/users';
import { formatDateDistanceToNow } from '@/utils/formatDateDistanceToNow';
import { formatRowDate } from '@/utils/formatRowDate';
import { cn } from '@/utils/tailwind';

import { TableCell, TableRow } from '../Table/Table';

export type UserManagementRowData = {
  id: UUID;
  name: string;
  title: string;
  iconUrl: string;
  department: string;
  role: string;
  apiRole: UserRole;
  lastActive?: string | Date | null;
};

type UserManagementRowRowProps = {
  rowData?: UserManagementRowData;
  isLoading?: boolean;
  isActive?: boolean;
  canChangeRole?: boolean;
  onClickSeeDetails?: () => void;
};

export const UserManagementRow: FC<UserManagementRowRowProps> = ({
  rowData,
  isActive,
  isLoading,
  canChangeRole = true,
  onClickSeeDetails,
}) => {
  const { t } = useTranslation(['settings', 'components']);
  const [isOpenActionsDropdown, setIsOpenActionsDropdown] = useState(false);

  if (isLoading) {
    return <UserManagementRowSkeleton />;
  }

  if (!rowData) {
    return null;
  }

  const { id, name, title, iconUrl, department, lastActive, apiRole } = rowData;

  const { full: lastActiveFullDate } = formatRowDate(lastActive);
  const lastActiveDistanceToNow = formatDateDistanceToNow(lastActiveFullDate);

  const handleSeeDetailsClick = () => {
    onClickSeeDetails?.();
    setIsOpenActionsDropdown(false);
  };

  return (
    <TableRow className={cn({ '!bg-custom-cyan-25': isActive })}>
      <TableCell className={USER_MANAGEMENT_TABLE_COLUMN_CLASSES.name}>
        <div className="flex min-w-0 items-center gap-2">
          <img
            src={iconUrl}
            alt={name}
            width={32}
            height={32}
            className="rounded-full object-cover"
          />
          <div>
            <div className="flex items-center gap-1.5 mb-1">
              <p className="text-body-14-semibold text-custom-warm-grey-950">{name}</p>
              {isActive && (
                <Tag
                  label={t('components:userManagementRow.leaderTag')}
                  size="small"
                  color="cyan"
                />
              )}
            </div>
            <p className="text-body-12-regular text-custom-warm-grey-600">{title}</p>
          </div>
        </div>
      </TableCell>
      <TableCell className={USER_MANAGEMENT_TABLE_COLUMN_CLASSES.department}>
        <Tag label={department} size="regular" color="grey" />
      </TableCell>
      <TableCell className={USER_MANAGEMENT_TABLE_COLUMN_CLASSES.role}>
        <UserManagementRoleCell userId={id} role={apiRole} canChangeRole={canChangeRole} />
      </TableCell>
      <TableCell className={USER_MANAGEMENT_TABLE_COLUMN_CLASSES.lastActive}>
        <p className="text-custom-warm-grey-500">{lastActiveDistanceToNow}</p>
      </TableCell>
      <TableCell className={USER_MANAGEMENT_TABLE_COLUMN_CLASSES.actions}>
        <Dropdown open={isOpenActionsDropdown} onOpenChange={setIsOpenActionsDropdown}>
          <DropdownMenuTrigger variant="transparent" className="cursor-pointer" asChild>
            <IconButton icon={<MoreVerticalIcon />} size="sm" variant="teritary" />
          </DropdownMenuTrigger>

          <DropdownMenuContent>
            <DropdownMenuSimpleItem type="button" onClick={handleSeeDetailsClick}>
              {t('components:userManagementRow.seeDetails')}
            </DropdownMenuSimpleItem>
          </DropdownMenuContent>
        </Dropdown>
      </TableCell>
    </TableRow>
  );
};
